"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import { MessageSquareMore, X, UserCircle, Users, ArrowRight, Search, Clock, CheckCircle2, AlertCircle, Sparkles, ChevronRight, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";
import { Loader2, Send } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { usePathname } from "next/navigation";
import ChatDockLauncher from "@/components/chat/ChatDockLauncher";
import { useCustomerSupportChat } from "@/hooks/useCustomerSupportChat";
import { useChatFaqSuggestions } from "@/hooks/useChatFaq";

const CustomerSupportDock = () => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const pathname = usePathname();
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<"assigned" | "pending">("assigned");
  const messageEndRef = useRef<HTMLDivElement | null>(null);
  const [showFaqSuggestions, setShowFaqSuggestions] = useState(false);
  const [selectedFaqIndex, setSelectedFaqIndex] = useState(-1);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const faqSuggestionsRef = useRef<HTMLDivElement | null>(null);
  const previousSuggestionsRef = useRef<typeof faqSuggestions>([]); // Keep previous suggestions while loading

  const {
    assignedConversations,
    pendingConversations,
    selectedConversationId,
    setSelectedConversationId,
    isLoadingConversations,
    conversationError,
    messages,
    isLoadingMessages,
    messagesError,
    messageInput,
    setMessageInput,
    isSending,
    searchTerm,
    setSearchTerm,
    handleSendMessage,
    handleAssignConversation,
    fetchMessages,
    fetchConversations,
  } = useCustomerSupportChat();

  // FAQ suggestions based on message input - Google-style instant autocomplete
  // Use selectedConversationId instead of currentConversation (defined later)
  // Reduced debounce to 200ms for faster suggestions as user types each letter
  const { suggestions: faqSuggestions, isLoading: isLoadingFaqs } = useChatFaqSuggestions(
    messageInput,
    { enabled: selectedConversationId !== null && messageInput.trim().length >= 1, debounceMs: 200 }
  );

  const shouldRender = !authLoading && isAuthenticated && pathname !== "/login";

  useEffect(() => {
    if (isOpen && messageEndRef.current) {
      setTimeout(() => {
        messageEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
  }, [isOpen, messages, selectedConversationId]);

  const currentConversation = useMemo(() => {
    return (
      assignedConversations.find((conv) => conv.id === selectedConversationId) ||
      pendingConversations.find((conv) => conv.id === selectedConversationId) ||
      null
    );
  }, [assignedConversations, pendingConversations, selectedConversationId]);

  // Check if input exactly matches any keyword (override behavior - keep suggestions visible)
  // OVERRIDE: If agent types exact keyword match, keep suggestions visible
  const hasExactKeywordMatch = useMemo(() => {
    if (!messageInput.trim() || faqSuggestions.length === 0) return false;
    
    const inputLower = messageInput.trim().toLowerCase();
    
    // Check if input exactly matches any keyword from suggestions
    for (const faq of faqSuggestions) {
      if (faq.keywords) {
        const keywords = faq.keywords.split(',').map(k => k.trim().toLowerCase());
        // Check for exact match or exact match with pluralization
        // Also check if input starts with a keyword (for multi-word queries like "branch locator")
        if (keywords.some(keyword => {
          // Exact match
          if (keyword === inputLower) return true;
          // Input matches keyword exactly (case-insensitive)
          if (keyword.toLowerCase() === inputLower) return true;
          // Pluralization: input is singular, keyword is plural
          if (keyword === inputLower + 's' || keyword === inputLower + 'es') return true;
          // Pluralization: input is plural, keyword is singular
          if (inputLower.endsWith('s') && keyword === inputLower.slice(0, -1)) return true;
          if (inputLower.endsWith('es') && keyword === inputLower.slice(0, -2)) return true;
          // Input starts with keyword (for multi-word like "branch locator")
          if (inputLower.startsWith(keyword + ' ') || inputLower === keyword) return true;
          // Keyword starts with input (partial match for autocomplete)
          if (keyword.startsWith(inputLower) && inputLower.length >= 2) return true;
          return false;
        })) {
          return true;
        }
      }
    }
    
    return false;
  }, [messageInput, faqSuggestions]);

  // Keep previous suggestions while loading (prevents flickering)
  // Google-style: Keep showing suggestions while user types new characters
  useEffect(() => {
    if (faqSuggestions.length > 0) {
      previousSuggestionsRef.current = faqSuggestions;
    }
    // Only clear previous suggestions if we explicitly want to hide (not on every change)
    // This prevents flickering when typing quickly
  }, [faqSuggestions]);
  
  // Track if user manually closed suggestions (to prevent auto-reopening)
  const manuallyClosedRef = useRef(false);
  const lastClosedInputRef = useRef<string>('');

  // Show/hide FAQ suggestions (Google-style: show as soon as there are suggestions)
  // Google behavior: Keep suggestions visible while typing, only hide on explicit outside click/escape
  useEffect(() => {
    const inputHasText = messageInput.trim().length >= 1;
    const hasSuggestions = faqSuggestions.length > 0;
    const hasPreviousSuggestions = previousSuggestionsRef.current.length > 0;
    const currentInput = messageInput.trim();
    
    // If user manually closed suggestions, don't auto-reopen unless they start typing new text
    // Reset the flag when input changes significantly (user is typing new query)
    if (manuallyClosedRef.current && inputHasText) {
      // Only reset if the input is significantly different (new search)
      if (currentInput !== lastClosedInputRef.current) {
        // Input changed, reset the flag to allow suggestions again
        manuallyClosedRef.current = false;
        lastClosedInputRef.current = '';
      } else {
        // Same input as when closed, keep closed
        return;
      }
    }
    
    // Google-style: Only hide if no conversation or no text (and never had suggestions)
    if (currentConversation === null || isSending) {
      if (isSending) {
        // Keep visible while sending (might want to select suggestion)
        return;
      }
      // Only hide if no conversation and no previous suggestions
      if (!hasPreviousSuggestions) {
        setShowFaqSuggestions(false);
      }
      return;
    }
    
    // If no text, hide only if we never had suggestions
    if (!inputHasText) {
      if (!hasPreviousSuggestions && !hasSuggestions) {
        setShowFaqSuggestions(false);
        manuallyClosedRef.current = false; // Reset when input is cleared
        lastClosedInputRef.current = ''; // Clear stored input
      }
      return;
    }
    
    // Google-style: ALWAYS show suggestions when there's text if:
    // 1. There are current suggestions, OR
    // 2. There are previous suggestions (keep showing while loading), OR
    // 3. Currently loading (show previous suggestions), OR
    // 4. Exact keyword match found
    if (hasSuggestions || hasPreviousSuggestions || isLoadingFaqs || hasExactKeywordMatch) {
      setShowFaqSuggestions(true);
    }
    
    // Reset selected index when suggestions change (but keep suggestions visible)
    if ((hasSuggestions || hasExactKeywordMatch) && showFaqSuggestions) {
      setSelectedFaqIndex(-1);
    }
  }, [messageInput, faqSuggestions, currentConversation, isSending, hasExactKeywordMatch, isLoadingFaqs, showFaqSuggestions]);

  // Highlight matching text in suggestions (Google-style)
  const highlightMatch = (text: string, query: string): React.ReactNode => {
    if (!query || !text) return text;
    const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${escapedQuery})`, 'gi');
    const parts = text.split(regex);
    return parts.map((part, index) => {
      // Check if this part matches the query (case-insensitive)
      if (part.toLowerCase() === query.toLowerCase()) {
        return (
          <mark key={index} className="bg-amber-200 dark:bg-amber-900/50 text-amber-900 dark:text-amber-100 px-0.5 rounded font-medium">
            {part}
          </mark>
        );
      }
      return <span key={index}>{part}</span>;
    });
  };

  // Handle FAQ selection - Google-style autofill
  const handleFaqSelect = (faq: typeof faqSuggestions[0]) => {
    // Autofill the answer smoothly
    setMessageInput(faq.answer);
    // Close suggestions immediately and prevent reopening
    setShowFaqSuggestions(false);
    setSelectedFaqIndex(-1);
    // Mark as manually closed to prevent auto-reopening for this specific input
    manuallyClosedRef.current = true;
    lastClosedInputRef.current = faq.answer.trim();
    // Clear previous suggestions to prevent them from showing again
    previousSuggestionsRef.current = [];
    // Focus and place cursor at end for easy editing
    setTimeout(() => {
      textareaRef.current?.focus();
      if (textareaRef.current) {
        const length = faq.answer.length;
        textareaRef.current.setSelectionRange(length, length);
      }
    }, 100);
  };

  // Keyboard navigation for FAQ suggestions
  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!showFaqSuggestions || faqSuggestions.length === 0) {
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        handleSendMessage();
      }
      return;
    }

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setSelectedFaqIndex((prev) => 
          prev < faqSuggestions.length - 1 ? prev + 1 : prev
        );
        // Scroll selected item into view
        setTimeout(() => {
          const selectedEl = faqSuggestionsRef.current?.querySelector(
            `[data-faq-index="${selectedFaqIndex + 1}"]`
          ) as HTMLElement;
          selectedEl?.scrollIntoView({ block: "nearest" });
        }, 0);
        break;
      case "ArrowUp":
        event.preventDefault();
        setSelectedFaqIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case "Enter":
        event.preventDefault();
        if (selectedFaqIndex >= 0 && selectedFaqIndex < faqSuggestions.length) {
          handleFaqSelect(faqSuggestions[selectedFaqIndex]);
        } else {
          handleSendMessage();
        }
        break;
      case "Escape":
        event.preventDefault();
        // OVERRIDE: Even with exact keyword match, ESC should close
        setShowFaqSuggestions(false);
        setSelectedFaqIndex(-1);
        manuallyClosedRef.current = true; // Mark as manually closed
        lastClosedInputRef.current = messageInput.trim(); // Store current input
        break;
      case "Tab":
        if (selectedFaqIndex >= 0 && selectedFaqIndex < faqSuggestions.length) {
          event.preventDefault();
          handleFaqSelect(faqSuggestions[selectedFaqIndex]);
        }
        break;
    }
  };

  const renderConversation = (conv: typeof assignedConversations[number], isPending = false) => {
    const timeAgo = conv.last_message_at 
      ? formatDistanceToNow(new Date(conv.last_message_at), { addSuffix: true })
      : "Just now";

    return (
      <button
        key={conv.id}
        onClick={() => setSelectedConversationId(conv.id)}
        className={cn(
          "flex w-full flex-col rounded-lg border p-3 text-left transition-all duration-200 text-xs group",
          "hover:shadow-md hover:scale-[1.01] active:scale-[0.99]",
            selectedConversationId === conv.id
            ? "border-[#0ba24c] bg-gradient-to-r from-[#0ba24c]/10 to-[#0ba24c]/5 shadow-sm"
            : "border-[#E3E8F5] bg-white hover:border-[#0ba24c]/40 hover:bg-[#e6f9ed]"
        )}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2.5 min-w-0 flex-1">
            <div className={cn(
              "rounded-full p-2 flex-shrink-0",
              isPending ? "bg-[#0ba24c]/10 dark:bg-[#0ba24c]/30" : "bg-[#0ba24c]/10"
            )}>
              <UserCircle className={cn(
                "h-4 w-4",
                isPending ? "text-[#0ba24c] dark:text-[#0ba24c]" : "text-[#0ba24c] dark:text-[#0ba24c]"
              )} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-sm truncate text-foreground">{conv.customer_name}</p>
              {conv.customer_email && (
                <p className="text-xs text-muted-foreground truncate mt-0.5">{conv.customer_email}</p>
              )}
            </div>
          </div>
          {isPending && (
            <Badge variant="outline" className="text-[10px] bg-[#0ba24c]/10 dark:bg-[#0ba24c]/30 border-[#0ba24c]/30 dark:border-[#0ba24c]/50 text-[#0ba24c] dark:text-[#0ba24c] flex-shrink-0">
              <Clock className="h-2.5 w-2.5 mr-1" />
              Pending
            </Badge>
          )}
        </div>
        
        <p className="mt-2.5 line-clamp-2 text-xs text-muted-foreground bg-muted/30 rounded px-2 py-1.5">
          {conv.last_message_preview || "No messages yet"}
        </p>
        
        <div className="mt-2.5 flex items-center justify-between">
          <p className="text-[10px] text-muted-foreground flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {timeAgo}
          </p>
          {conv.status === "active" && (
            <Badge variant="outline" className="text-[9px] bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300">
              <CheckCircle2 className="h-2.5 w-2.5 mr-1" />
              Active
            </Badge>
          )}
        </div>
        
        {isPending && (
          <Button
            size="sm"
            variant="outline"
            className="mt-3 text-xs h-7 bg-[#0ba24c]/10 hover:bg-[#0ba24c]/20 dark:bg-[#0ba24c]/30 dark:hover:bg-[#0ba24c]/40 border-[#0ba24c]/30 dark:border-[#0ba24c]/50 text-[#0ba24c] dark:text-[#0ba24c]"
            onClick={(event) => {
              event.stopPropagation();
              handleAssignConversation(conv.id);
            }}
          >
            Claim Conversation <ArrowRight className="ml-1.5 h-3 w-3" />
          </Button>
        )}
      </button>
    );
  };

  const renderMessages = () => {
    if (!currentConversation) {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-3 text-center text-muted-foreground p-6">
          <div className="rounded-full bg-[#0ba24c]/10 p-4">
            <Users className="h-8 w-8 text-[#0ba24c]" />
          </div>
          <div className="space-y-1">
            <p className="text-sm font-semibold text-[#0ba24c]">Select a conversation to begin</p>
            <p className="text-xs text-muted-foreground">Choose from the list on the left</p>
          </div>
        </div>
      );
    }

    if (messagesError) {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-2 text-center text-destructive p-4">
          <AlertCircle className="h-6 w-6 mb-2" />
          <p className="text-sm font-medium">{messagesError}</p>
          <p className="text-xs text-muted-foreground">Please try again later</p>
        </div>
      );
    }

    if (isLoadingMessages) {
      return (
        <div className="flex h-full items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
            <p className="text-xs text-muted-foreground">Loading messages...</p>
          </div>
        </div>
      );
    }

    if (!messages.length) {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-3 text-center text-muted-foreground p-6">
          <div className="rounded-full bg-[#0ba24c]/10 p-4">
            <MessageSquareMore className="h-8 w-8 text-[#0ba24c]" />
          </div>
          <div className="space-y-1">
            <p className="text-sm font-semibold text-[#0ba24c]">No messages yet</p>
            <p className="text-xs text-muted-foreground">Introduce yourself to {currentConversation.customer_name} 👋</p>
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-3 text-sm px-1">
        {messages.map((msg, index) => {
          const isAgent = msg.sender_type === "agent";
          const isAi = msg.sender_type === "ai";
          const isCustomer = msg.sender_type === "customer";
          const showTimestamp = index === 0 || 
            new Date(msg.created_at).getTime() - new Date(messages[index - 1].created_at).getTime() > 300000; // 5 minutes
          
          return (
            <div key={msg.id} className="space-y-1">
              {showTimestamp && (
                <div className="flex justify-center py-2">
                  <span className="text-[10px] text-muted-foreground bg-muted/50 px-2 py-1 rounded-full">
                    {new Date(msg.created_at).toLocaleDateString()} {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              )}
              <div className={cn("flex w-full gap-2", isAgent ? "justify-end" : "justify-start")}>
                {/* Avatar for AI or Customer (not agent) */}
                {!isAgent && (
                  <Avatar className="h-6 w-6 mt-1 flex-shrink-0">
                    {isAi ? (
                      <AvatarFallback className="bg-gradient-to-br from-purple-100 to-blue-100 dark:from-purple-900/30 dark:to-blue-900/30 text-purple-700 dark:text-purple-300 text-[10px]">
                        <Sparkles className="h-3 w-3" />
                      </AvatarFallback>
                    ) : (
                      <AvatarFallback className="bg-[#0ba24c]/10 dark:bg-[#0ba24c]/30 text-[#0ba24c] dark:text-[#0ba24c] text-[10px]">
                        {currentConversation.customer_name[0].toUpperCase()}
                      </AvatarFallback>
                    )}
                  </Avatar>
                )}
                <div className={cn("flex flex-col max-w-[75%]", isAgent ? "items-end" : "items-start")}>
                  {/* Sender label */}
                  {isAi && (
                    <span className="text-[10px] text-muted-foreground mb-1 px-2 flex items-center gap-1">
                      <Sparkles className="h-2.5 w-2.5 text-purple-500 dark:text-purple-400" />
                      AI Assistant
                    </span>
                  )}
                  {isCustomer && (
                    <span className="text-[10px] text-muted-foreground mb-1 px-2">
                      {currentConversation.customer_name.split(" ")[0]}
                    </span>
                  )}
                  {isAgent && (
                    <span className="text-[10px] text-muted-foreground mb-1 px-2">
                      You
                    </span>
                  )}
                  {/* Message bubble */}
                  <div
                    className={cn(
                      "rounded-2xl px-4 py-2.5 text-sm shadow-sm transition-all duration-200",
                      "break-words whitespace-pre-wrap",
                      isAgent
                        ? "bg-gradient-to-br from-[#0ba24c] to-[#0ba24c]/90 text-white rounded-br-md"
                        : isAi
                        ? "bg-gradient-to-br from-purple-50 to-blue-50 dark:from-purple-950/50 dark:to-blue-950/50 border border-purple-200/50 dark:border-purple-800/50 text-foreground rounded-bl-md"
                        : "bg-card border border-border/50 text-foreground rounded-bl-md"
                    )}
                  >
                    <p className="leading-relaxed">{msg.message}</p>
                  </div>
                  <div className="mt-1 flex items-center gap-2 text-[10px] text-muted-foreground px-2">
                    <span>{formatDistanceToNow(new Date(msg.created_at), { addSuffix: true })}</span>
                  </div>
                </div>
                {isAgent && (
                  <Avatar className="h-6 w-6 mt-1 flex-shrink-0">
                    <AvatarFallback className="bg-primary/10 text-primary text-[10px]">
                      A
                    </AvatarFallback>
                  </Avatar>
                )}
              </div>
            </div>
          );
        })}
        <div ref={messageEndRef} className="h-2" />
      </div>
    );
  };

  if (!shouldRender) {
    return null;
  }

  return (
    <>
      {/* Floating Action Button */}
      <div className="fixed bottom-6 right-[90px] sm:right-[120px] lg:right-[150px] z-50 flex flex-col items-end">
        <ChatDockLauncher
          label="Customer Chat"
          icon={<MessageSquareMore className="h-5 w-5 text-white" />}
          badgeCount={pendingConversations.length}
          onClick={() => setIsOpen(true)}
          hidden={isOpen}
          backgroundColor="#0ba24c"
          textColor="#ffffff"
          borderColor="#08903f"
          iconBackgroundColor="#0ba24c"
        />
      </div>

      {/* Chat Window */}
      {isOpen && (
        <Card 
          className={cn(
            "fixed bottom-8 right-32 z-50 flex h-[680px] w-[700px] flex-col border shadow-2xl",
            "bg-white rounded-2xl border border-[#E1E8F5]",
            "animate-in slide-in-from-bottom-4 fade-in-0"
          )}
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b bg-gradient-to-r from-[#0ba24c]/10 dark:from-[#0ba24c]/20 to-transparent px-5 py-4">
            <div className="flex items-center gap-3">
              <div className="rounded-full bg-[#0ba24c]/10 dark:bg-[#0ba24c]/30 p-2">
                <MessageSquareMore className="h-5 w-5 text-[#0ba24c] dark:text-[#0ba24c]" />
              </div>
              <div>
                <p className="text-base font-semibold text-foreground">Customer Support</p>
                <p className="text-xs text-muted-foreground">
                  {currentConversation 
                    ? `Chatting with ${currentConversation.customer_name}`
                    : `Assist customers in queue${pendingConversations.length > 0 ? ` • ${pendingConversations.length} waiting` : ""}`
                  }
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8 hover:bg-muted"
                onClick={() => {
                  if (selectedConversationId) {
                    fetchMessages();
                  }
                  fetchConversations();
                }}
                disabled={isLoadingMessages || isLoadingConversations}
                title="Refresh"
              >
                <RefreshCw className={cn("h-4 w-4", (isLoadingMessages || isLoadingConversations) && "animate-spin")} />
              </Button>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8 hover:bg-destructive/10 hover:text-destructive"
                onClick={() => setIsOpen(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex flex-1 overflow-hidden">
            {/* Sidebar - Conversation List */}
            <div className="w-72 border-r border-[#E1E8F5] bg-white flex flex-col">
              <div className="p-3 border-b bg-[#e1f5e9]">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#0ba24c]" />
                  <Input
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    placeholder="Search conversations..."
                    className="h-9 pl-8 text-xs border-[#0ba24c]/40 focus-visible:ring-[#0ba24c]/30 focus-visible:ring-offset-0"
                  />
                </div>
              </div>

              {/* Tabs */}
              <div className="p-3 border-b bg-white space-y-2">
                <div className="flex items-center gap-2 rounded-lg bg-[#EFF2FB] p-1">
                  <button
                    className={cn(
                      "flex-1 rounded-md px-2 py-1.5 text-xs font-medium transition-all",
                      activeTab === "assigned"
                        ? "bg-white text-[#0ba24c] shadow-sm"
                        : "text-muted-foreground hover:text-foreground"
                    )}
                    onClick={() => setActiveTab("assigned")}
                  >
                    My Chats
                  </button>
                  <button
                    className={cn(
                      "flex-1 rounded-md px-2 py-1.5 text-xs font-medium transition-all relative",
                      activeTab === "pending"
                        ? "bg-white text-[#0ba24c] shadow-sm"
                        : "text-muted-foreground hover:text-foreground"
                    )}
                    onClick={() => setActiveTab("pending")}
                  >
                    Queue
                    {pendingConversations.length > 0 && (
                      <Badge className="ml-1.5 bg-[#0ba24c] text-white px-1 py-0 h-4 text-[9px] font-bold">
                        {pendingConversations.length > 99 ? "99+" : pendingConversations.length}
                      </Badge>
                    )}
                  </button>
                </div>
              </div>

              {/* Conversation List */}
              <div className="flex-1 overflow-y-auto p-3 space-y-2">
                {conversationError && (
                  <div className="text-xs text-destructive p-2 bg-destructive/10 rounded-lg">
                    {conversationError}
                  </div>
                )}

                {isLoadingConversations ? (
                  <div className="flex flex-1 items-center justify-center py-8">
                    <Loader2 className="h-5 w-5 animate-spin text-primary" />
                  </div>
                ) : (
                  <>
                    {activeTab === "assigned" && (
                      <>
                        {assignedConversations.length ? (
                          assignedConversations.map((conv) => renderConversation(conv))
                        ) : (
                          <div className="py-8 text-center">
                            <CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-muted-foreground opacity-50" />
                            <p className="text-xs text-muted-foreground">No assigned chats</p>
                          </div>
                        )}
                      </>
                    )}
                    {activeTab === "pending" && (
                      <>
                        {pendingConversations.length ? (
                          pendingConversations.map((conv) => renderConversation(conv, true))
                        ) : (
                          <div className="py-8 text-center">
                            <Clock className="h-8 w-8 mx-auto mb-2 text-muted-foreground opacity-50" />
                            <p className="text-xs text-muted-foreground">No customers waiting</p>
                          </div>
                        )}
                      </>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Chat Area */}
            <div className="flex flex-1 flex-col bg-gradient-to-b from-white to-[#dff4e7]">
              {/* Customer Info Bar (if conversation selected) */}
              {currentConversation && (
                <div className="border-b bg-white px-4 py-2.5">
                  <div className="flex items-center gap-3">
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="bg-[#0ba24c]/10 dark:bg-[#0ba24c]/30 text-[#0ba24c] dark:text-[#0ba24c] text-xs">
                        {currentConversation.customer_name[0].toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0 flex items-center gap-2">
                      <p className="text-sm font-semibold truncate">{currentConversation.customer_name}</p>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-6 w-6 hover:bg-muted rounded-full flex-shrink-0"
                        onClick={() => {
                          fetchMessages();
                          fetchConversations();
                        }}
                        disabled={isLoadingMessages}
                        title="Refresh messages"
                      >
                        <RefreshCw className={cn("h-3.5 w-3.5 text-muted-foreground", isLoadingMessages && "animate-spin")} />
                      </Button>
                    </div>
                    <div className="flex items-center gap-2">
                      {currentConversation.customer_email && (
                        <p className="text-xs text-muted-foreground truncate max-w-[150px]">{currentConversation.customer_email}</p>
                      )}
                      {currentConversation.status === "pending" && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-xs h-7"
                          onClick={() => handleAssignConversation(currentConversation.id)}
                        >
                          Claim
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAwIDEwIEwgNDAgMTAgTSAxMCAwIEwgMTAgNDAiIGZpbGw9Im5vbmUiIHN0cm9rZT0iIzkwYTJlYSIgc3Ryb2tlLXdpZHRoPSIwLjQiIG9wYWNpdHk9IjAuMDIiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')]">
                <div className="max-w-full">{renderMessages()}</div>
              </div>

              {/* Input Area */}
              <div className="border-t bg-white p-4 space-y-3 relative">
                {/* FAQ Suggestions Dropdown - Google-style Autocomplete */}
                {showFaqSuggestions && (faqSuggestions.length > 0 || (isLoadingFaqs && previousSuggestionsRef.current.length > 0)) && (
                  <Card 
                    ref={faqSuggestionsRef}
                    onMouseDown={(e) => {
                      // Prevent textarea from losing focus when clicking suggestions
                      e.preventDefault();
                    }}
                    className="absolute bottom-full left-0 right-0 mb-2 max-h-[400px] overflow-hidden shadow-2xl border-2 z-50 animate-in slide-in-from-bottom-2 fade-in-0 bg-background"
                  >
                    <div className="p-2.5 border-b bg-gradient-to-r from-[#0ba24c]/10 dark:from-[#0ba24c]/20 to-transparent sticky top-0">
                      <div className="flex items-center gap-2 px-2">
                        <Sparkles className="h-4 w-4 text-[#0ba24c] dark:text-[#0ba24c]" />
                        <p className="text-xs font-semibold text-foreground">FAQ Suggestions</p>
                        <Badge variant="secondary" className="text-[9px] h-4 px-1.5">
                          {(faqSuggestions.length > 0 ? faqSuggestions : previousSuggestionsRef.current).length}
                        </Badge>
                        {isLoadingFaqs && (
                          <Loader2 className="h-3 w-3 animate-spin text-muted-foreground ml-auto" />
                        )}
                      </div>
                    </div>
                    <div className="p-1.5 space-y-0.5 max-h-[350px] overflow-y-auto custom-scrollbar">
                      {(faqSuggestions.length > 0 ? faqSuggestions : previousSuggestionsRef.current).slice(0, 8).map((faq, index) => (
                        <button
                          key={faq.id}
                          data-faq-index={index}
                          onClick={() => handleFaqSelect(faq)}
                          onMouseDown={(e) => {
                            // Prevent textarea from losing focus when clicking suggestions
                            e.preventDefault();
                          }}
                          className={cn(
                            "w-full text-left p-3 rounded-lg transition-all duration-150 group",
                            "hover:shadow-md border",
                            selectedFaqIndex === index
                              ? "bg-[#0ba24c]/10 dark:bg-[#0ba24c]/30 border-[#0ba24c]/30 dark:border-[#0ba24c]/50 shadow-sm"
                              : "bg-background hover:bg-[#0ba24c]/5 dark:hover:bg-[#0ba24c]/10 border-transparent hover:border-[#0ba24c]/20 dark:hover:border-[#0ba24c]/30"
                          )}
                          onMouseEnter={() => setSelectedFaqIndex(index)}
                        >
                          <div className="flex items-start gap-3">
                            <div className="flex-shrink-0 mt-0.5">
                              <div className={cn(
                                "w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold transition-colors",
                                selectedFaqIndex === index
                                  ? "bg-[#0ba24c] text-white"
                                  : "bg-muted text-muted-foreground group-hover:bg-[#0ba24c]/20 dark:group-hover:bg-[#0ba24c]/40"
                              )}>
                                {index + 1}
                              </div>
                            </div>
                            <div className="flex-1 min-w-0">
                              {/* Show matched keywords first (Google-style) */}
                              {faq.keywords && (
                                <div className="flex items-center gap-1.5 mb-1.5 flex-wrap">
                                  {faq.keywords.split(',').map((keyword, kIndex) => {
                                    const keywordTrimmed = keyword.trim().toLowerCase();
                                    const searchLower = messageInput.trim().toLowerCase();
                                    const isMatched = keywordTrimmed.includes(searchLower) || searchLower.includes(keywordTrimmed);
                                    if (isMatched && searchLower.length > 0) {
                                      return (
                                        <Badge 
                                          key={kIndex}
                                          variant="outline" 
                                          className={cn(
                                            "text-[9px] h-4 px-1.5 font-medium",
                                            selectedFaqIndex === index 
                                              ? "bg-[#0ba24c]/20 dark:bg-[#0ba24c]/50 border-[#0ba24c]/40 text-[#0ba24c] dark:text-[#0ba24c]"
                                              : "bg-[#0ba24c]/10 dark:bg-[#0ba24c]/30 border-[#0ba24c]/30 dark:border-[#0ba24c]/50 text-[#0ba24c] dark:text-[#0ba24c]"
                                          )}
                                        >
                                          {keyword.trim()}
                                        </Badge>
                                      );
                                    }
                                    return null;
                                  })}
                                </div>
                              )}
                              {/* Show answer prominently (this is what gets autofilled) */}
                              <p className={cn(
                                "text-sm leading-relaxed mb-1.5 line-clamp-2",
                                selectedFaqIndex === index
                                  ? "text-[#0ba24c] dark:text-[#0ba24c] font-medium"
                                  : "text-foreground font-medium group-hover:text-[#0ba24c] dark:group-hover:text-[#0ba24c]"
                              )}>
                                {highlightMatch(faq.answer, messageInput.trim())}
                              </p>
                              {/* Show question as secondary info */}
                              <p className={cn(
                                "text-xs line-clamp-1 mb-1.5",
                                selectedFaqIndex === index
                                  ? "text-[#0ba24c] dark:text-[#0ba24c]"
                                  : "text-muted-foreground"
                              )}>
                                Q: {faq.question}
                              </p>
                              {faq.category && (
                                <Badge 
                                  variant="outline" 
                                  className={cn(
                                    "text-[9px] h-4 px-1.5",
                                    selectedFaqIndex === index && "border-[#0ba24c]/40 text-[#0ba24c] dark:text-[#0ba24c]"
                                  )}
                                >
                                  {faq.category}
                                </Badge>
                              )}
                            </div>
                            <ChevronRight className={cn(
                              "h-4 w-4 flex-shrink-0 mt-0.5 transition-all",
                              selectedFaqIndex === index
                                ? "text-[#0ba24c] dark:text-[#0ba24c] opacity-100 translate-x-0"
                                : "text-muted-foreground opacity-0 group-hover:opacity-100 translate-x-[-4px] group-hover:translate-x-0"
                            )} />
                          </div>
                        </button>
                      ))}
                    </div>
                    {(faqSuggestions.length > 0 ? faqSuggestions : previousSuggestionsRef.current).length > 8 && (
                      <div className="p-2 border-t bg-muted/30 text-center">
                        <p className="text-xs text-muted-foreground">
                          Showing 8 of {(faqSuggestions.length > 0 ? faqSuggestions : previousSuggestionsRef.current).length} suggestions • Continue typing to refine
                        </p>
                      </div>
                    )}
                    <div className="p-2 border-t bg-muted/50 text-center">
                      <p className="text-[10px] text-muted-foreground">
                        Use <kbd className="px-1 py-0.5 bg-background rounded text-[9px]">↑↓</kbd> to navigate • 
                        <kbd className="px-1 py-0.5 bg-background rounded text-[9px] ml-1">Enter</kbd> to select • 
                        <kbd className="px-1 py-0.5 bg-background rounded text-[9px] ml-1">Esc</kbd> to close
                      </p>
                    </div>
                  </Card>
                )}

                <div className="relative">
                  <Textarea
                    ref={textareaRef}
                    value={messageInput}
                    onChange={(event) => {
                      setMessageInput(event.target.value);
                    }}
                    onFocus={() => {
                      // Google-style: Always show suggestions when focused if there's input
                      if (messageInput.trim().length >= 1) {
                        setShowFaqSuggestions(true);
                      }
                    }}
                    onBlur={(e) => {
                      // Google-style: Don't hide immediately on blur
                      // Only hide if clicking outside suggestions AND not loading AND no exact match
                      
                      // NEVER hide if:
                      // 1. Exact keyword match found
                      // 2. Currently loading
                      // 3. There's text and we have (current or previous) suggestions
                      if (hasExactKeywordMatch && messageInput.trim().length >= 1) {
                        return; // Keep visible for exact matches
                      }
                      
                      if (isLoadingFaqs && messageInput.trim().length >= 1) {
                        return; // Keep visible while loading
                      }
                      
                      // Check if clicking inside suggestions dropdown (should not hide)
                      const relatedTarget = e.relatedTarget as HTMLElement;
                      const activeElement = document.activeElement as HTMLElement;
                      
                      if (faqSuggestionsRef.current?.contains(relatedTarget) || 
                          faqSuggestionsRef.current?.contains(activeElement)) {
                        return; // Don't hide if clicking in suggestions
                      }
                      
                      // Google-style: Longer delay to allow click events and check if still focused elsewhere
                      setTimeout(() => {
                        const currentActive = document.activeElement as HTMLElement;
                        
                        // Don't hide if:
                        // - Focus is in suggestions
                        // - Exact keyword match
                        // - Currently loading
                        // - Input has text and we have suggestions
                        if (faqSuggestionsRef.current?.contains(currentActive)) {
                          return; // Still clicking in suggestions
                        }
                        
                        if (hasExactKeywordMatch && messageInput.trim().length >= 1) {
                          return; // Keep visible for exact matches
                        }
                        
                        if (isLoadingFaqs && messageInput.trim().length >= 1) {
                          return; // Keep visible while loading
                        }
                        
                        // Only hide if really clicking outside and no special conditions
                        if (!textareaRef.current?.contains(currentActive)) {
                          setShowFaqSuggestions(false);
                        }
                      }, 500); // Longer delay like Google
                    }}
                    onKeyDown={handleKeyDown}
                    placeholder={
                      currentConversation
                        ? `Type your reply... (FAQ suggestions will appear automatically)`
                        : "Select a conversation"
                    }
                    rows={3}
                    disabled={!currentConversation || isSending}
                    className={cn(
                      "resize-none text-sm border-border/50 focus-visible:ring-primary/20",
                      "min-h-[60px] max-h-[120px]",
                      showFaqSuggestions && "border-[#0ba24c]/30 dark:border-[#0ba24c]/50"
                    )}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <p className="text-[10px] text-muted-foreground">
                    Press <kbd className="px-1.5 py-0.5 bg-muted rounded text-[10px]">Enter</kbd> to send • 
                    <kbd className="px-1.5 py-0.5 bg-muted rounded text-[10px] ml-1">Shift + Enter</kbd> for newline
                  </p>
                  <Button
                    size="sm"
                    onClick={handleSendMessage}
                    disabled={!currentConversation || !messageInput.trim() || isSending}
                    className="bg-[#0ba24c] hover:bg-[#08903f] text-white shadow-sm"
                  >
                    {isSending ? (
                      <>
                        <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                        Sending
                      </>
                    ) : (
                      <>
                        <Send className="mr-2 h-3.5 w-3.5" />
                        Send
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </Card>
      )}
    </>
  );
};

export default CustomerSupportDock;
